import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Moment from "moment";
import { extendMoment } from "moment-range";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const SERVER_URL = process.env.NODE_ENV === "development" ? "http://localhost:5000" : "";

function RazorpayTransactions() {
    const moment = extendMoment(Moment);
    const navigate = useNavigate();

    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [methodFilter, setMethodFilter] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [totalCount, setTotalCount] = useState(0);
    const [fetchCount, setFetchCount] = useState(100);

    useEffect(() => {
        const email = localStorage.getItem("email");
        const password = localStorage.getItem("password");
        const remember = localStorage.getItem("remember");
        if (remember === "false" || !email || !password) {
            navigate("/signin");
        }
    }, [navigate]);

    const fetchPayments = useCallback(async (count = fetchCount) => {
        setLoading(true);
        setError("");
        try {
            const res = await fetch(
                `${SERVER_URL}/admin/payments?count=${count}`,
                { headers: { "x-admin-token": "youvati_admin_2024" } }
            );
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            const data = await res.json();
            setPayments(data.items || []);
            setTotalCount(data.count || 0);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [fetchCount]);

    useEffect(() => {
        fetchPayments();
    }, [fetchPayments]);

    const filtered = payments.filter((p) => {
        const searchable = `${p.id || ""} ${p.contact || ""} ${p.email || ""} ${p.description || ""} ${p.order_id || ""}`.toLowerCase();
        const matchSearch = searchable.includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === "" || p.status === statusFilter;
        const matchMethod = methodFilter === "" || p.method === methodFilter;
        return matchSearch && matchStatus && matchMethod;
    });

    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentRows = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / rowsPerPage);

    const uniqueMethods = [...new Set(payments.map((p) => p.method).filter(Boolean))].sort();

    const statusBadge = (status) => {
        const map = {
            captured: "success",
            authorized: "primary",
            failed: "danger",
            refunded: "warning",
            created: "secondary",
        };
        return (
            <span className={`badge bg-${map[status] || "secondary"}`} style={{ fontSize: "11px" }}>
                {status}
            </span>
        );
    };

    const rupees = (paise) =>
        paise != null ? `₹ ${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` : "—";

    const formatDate = (ts) =>
        ts ? moment.unix(ts).format("DD/MM/YYYY, h:mm A") : "—";

    const totalCaptured = payments
        .filter((p) => p.status === "captured")
        .reduce((sum, p) => sum + (p.amount || 0), 0);

    const handleExport = () => {
        const rows = filtered.map((p, i) => ({
            "Sr.": i + 1,
            "Payment ID": p.id,
            "Order ID": p.order_id || "",
            "Amount": p.amount != null ? p.amount / 100 : "",
            "Currency": p.currency || "INR",
            "Status": p.status,
            "Method": p.method,
            "Contact": p.contact,
            "Email": p.email,
            "Description": p.description,
            "Date": formatDate(p.created_at),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Razorpay");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
            new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `Razorpay_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
        );
    };

    return (
        <div className="p-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                    Razorpay Transactions&nbsp;
                    <span className="text-muted" style={{ fontSize: "13px", fontWeight: 400 }}>
                        Showing {filtered.length} / Fetched {payments.length}
                    </span>
                </h5>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* Summary Cards */}
            <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                    <div className="card text-center p-2 border-success">
                        <div style={{ fontSize: "11px", color: "#6c757d" }}>Total Captured</div>
                        <div style={{ fontSize: "18px", fontWeight: 700, color: "#198754" }}>{rupees(totalCaptured)}</div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card text-center p-2 border-primary">
                        <div style={{ fontSize: "11px", color: "#6c757d" }}>Total Transactions</div>
                        <div style={{ fontSize: "18px", fontWeight: 700 }}>{payments.length}</div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card text-center p-2 border-danger">
                        <div style={{ fontSize: "11px", color: "#6c757d" }}>Failed</div>
                        <div style={{ fontSize: "18px", fontWeight: 700, color: "#dc3545" }}>
                            {payments.filter((p) => p.status === "failed").length}
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card text-center p-2">
                        <div style={{ fontSize: "11px", color: "#6c757d" }}>Captured</div>
                        <div style={{ fontSize: "18px", fontWeight: 700, color: "#198754" }}>
                            {payments.filter((p) => p.status === "captured").length}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="row g-2 mb-3">
                <div className="col-md-4">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Payment ID, contact, email, order ID…"
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>
                <div className="col-md-2">
                    <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All Statuses</option>
                        <option value="captured">Captured</option>
                        <option value="authorized">Authorized</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                        <option value="created">Created</option>
                    </select>
                </div>
                <div className="col-md-2">
                    <select
                        className="form-select"
                        value={methodFilter}
                        onChange={(e) => { setMethodFilter(e.target.value); setCurrentPage(1); }}
                    >
                        <option value="">All Methods</option>
                        {uniqueMethods.map((m) => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-1">
                    <button
                        className="btn btn-secondary w-100"
                        onClick={() => { setSearchTerm(""); setStatusFilter(""); setMethodFilter(""); setCurrentPage(1); }}
                    >
                        Reset
                    </button>
                </div>
                <div className="col-md-2">
                    <button
                        className="btn btn-success w-100"
                        onClick={handleExport}
                        disabled={filtered.length === 0}
                    >
                        Export Excel
                    </button>
                </div>
                <div className="col-md-1">
                    <button
                        className="btn btn-primary w-100"
                        onClick={() => fetchPayments(fetchCount)}
                        disabled={loading}
                    >
                        {loading ? "…" : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Fetch more row */}
            <div className="d-flex align-items-center gap-2 mb-3">
                <span style={{ fontSize: "13px" }}>Fetch last</span>
                {[100, 200, 500, 1000, 2000].map((n) => (
                    <button
                        key={n}
                        className={`btn btn-sm ${fetchCount === n ? "btn-dark" : "btn-outline-dark"}`}
                        onClick={() => { setFetchCount(n); fetchPayments(n); }}
                        disabled={loading}
                    >
                        {n}
                    </button>
                ))}
                <span style={{ fontSize: "12px", color: "#6c757d" }}>transactions</span>
            </div>

            {/* Error */}
            {error && (
                <div className="alert alert-danger py-2" style={{ fontSize: "13px" }}>
                    {error}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Loading from Razorpay…
                </div>
            )}

            {/* Table */}
            {!loading && (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle" style={{ fontSize: "12px" }}>
                        <thead className="table-dark">
                            <tr>
                                <th>#</th>
                                <th>Payment ID</th>
                                <th>Order ID</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Method</th>
                                <th>Contact</th>
                                <th>Email / Description</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentRows.length === 0 ? (
                                <tr>
                                    <td colSpan="9" className="text-center py-4 text-muted">
                                        No transactions found
                                    </td>
                                </tr>
                            ) : currentRows.map((p, idx) => (
                                <tr key={p.id}>
                                    <td>{indexOfFirst + idx + 1}</td>
                                    <td style={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                                        <a
                                            href={`https://dashboard.razorpay.com/app/payments/${p.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {p.id}
                                        </a>
                                    </td>
                                    <td style={{ fontFamily: "monospace", whiteSpace: "nowrap", fontSize: "11px", color: "#666" }}>
                                        {p.order_id || "—"}
                                    </td>
                                    <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                        {rupees(p.amount)}
                                    </td>
                                    <td>{statusBadge(p.status)}</td>
                                    <td style={{ textTransform: "capitalize" }}>{p.method || "—"}</td>
                                    <td style={{ whiteSpace: "nowrap" }}>{p.contact || "—"}</td>
                                    <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        <span title={p.email || p.description || ""}>
                                            {p.email || p.description || "—"}
                                        </span>
                                    </td>
                                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(p.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {!loading && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <div>
                        Rows per page:
                        <select
                            className="form-select d-inline-block w-auto ms-2"
                            value={rowsPerPage}
                            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            {[10, 25, 50, 100].map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <button
                            className="btn btn-outline-secondary me-2"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span>Page {currentPage} of {totalPages || 1}</span>
                        <button
                            className="btn btn-outline-secondary ms-2"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RazorpayTransactions;
